// useTheme — single source of truth for theme state + DOM application.
//
// Theme model:
//   - 'system' (default) — follows prefers-color-scheme reactively
//   - 'light' / 'dark'   — explicit override
//
// Persistence layers (in priority order, read):
//   1. window.localStorage 'hr-theme'  — applied by no-FOUC script in
//      index.html before React mounts. Also written here so anon users
//      get persistence too.
//   2. /api/me theme_preference        — reconciled once the session
//      settles. If it differs from localStorage, the DB value wins and
//      we update localStorage to match.
//
// Writes:
//   - localStorage always
//   - PUT /api/me/theme only when authenticated (best-effort; localStorage
//     stays authoritative for the visible UI even if the DB write 401s).
//
// DOM application:
//   - Adds/removes 'dark' class on document.documentElement based on the
//     RESOLVED theme (which collapses 'system' to the matchMedia answer).
//   - When theme === 'system', re-subscribes to the matchMedia change so
//     the page flips when the OS toggles dark mode mid-session.

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/auth'

export type Theme = 'system' | 'light' | 'dark'
export type Resolved = 'light' | 'dark'

const STORAGE_KEY = 'hr-theme'
const VALID: ReadonlySet<Theme> = new Set(['system', 'light', 'dark'])

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw && VALID.has(raw as Theme)) return raw as Theme
  } catch {
    /* localStorage unavailable */
  }
  return 'system'
}

function systemPreference(): Resolved {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(t: Theme): Resolved {
  return t === 'system' ? systemPreference() : t
}

function applyClass(resolved: Resolved): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

interface MeThemeResponse {
  profile?: { theme_preference?: Theme }
}

export function useTheme() {
  const { data: session, isPending: sessionPending } = useSession()
  const [theme, setThemeState] = useState<Theme>(readStored)
  const [resolved, setResolvedState] = useState<Resolved>(() => resolveTheme(readStored()))

  // Reactive system-pref subscription. Only relevant when theme === 'system';
  // for explicit light/dark the matchMedia signal is ignored.
  useEffect(() => {
    if (theme !== 'system') {
      const r = resolveTheme(theme)
      setResolvedState(r)
      applyClass(r)
      return
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const update = () => {
      const r: Resolved = mq.matches ? 'dark' : 'light'
      setResolvedState(r)
      applyClass(r)
    }
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [theme])

  // Once the auth session settles, reconcile localStorage with the DB.
  // DB wins; localStorage is updated to match. If the user signs out
  // mid-session, we keep whatever's in localStorage (don't reset).
  useEffect(() => {
    if (sessionPending || !session?.user) return
    let cancelled = false
    apiFetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: MeThemeResponse | null) => {
        if (cancelled) return
        const dbTheme = data?.profile?.theme_preference
        if (dbTheme && VALID.has(dbTheme) && dbTheme !== theme) {
          setThemeState(dbTheme)
          try {
            localStorage.setItem(STORAGE_KEY, dbTheme)
          } catch {
            /* localStorage unavailable */
          }
        }
      })
      .catch(() => {
        /* leave local state alone on /api/me failure */
      })
    return () => {
      cancelled = true
    }
    // We deliberately depend only on the session — re-running on every
    // theme change would clobber the user's intent with stale DB reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionPending])

  const setTheme = useCallback(
    async (next: Theme) => {
      if (!VALID.has(next)) return
      setThemeState(next)
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* localStorage unavailable */
      }
      if (session?.user) {
        try {
          await apiFetch('/api/me/theme', {
            method: 'PUT',
            body: JSON.stringify({ theme_preference: next }),
          })
        } catch {
          /* swallow — localStorage is still authoritative for the UI */
        }
      }
    },
    [session],
  )

  return { theme, resolved, setTheme }
}
